import type { Guest } from "./types";
import { canonTable } from "./tableName";
import { apartIds, ruleBreaks } from "./seatingRules";

export const seatsFor = (g: Pick<Guest, "plusOnes">) => 1 + Math.max(0, Math.floor(g.plusOnes ?? 0));
export { canonTable };
const norm = (s: string | undefined) => canonTable(s).toLowerCase();
export const attending = (guests: readonly Guest[]) => guests.filter((g) => g.status !== "declined");

export interface TableView { name: string; guests: Guest[]; used: number; capacity: number; free: number }

const numericAware = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/** Tables are every name guests already use, plus Table 1..count. */
export function tableNames(guests: readonly Guest[], count: number): string[] {
  const seen = new Map<string, string>();
  for (let i = 1; i <= Math.max(0, count); i++) seen.set(`table ${i}`, `Table ${i}`);
  for (const g of attending(guests)) { const t = canonTable(g.table); if (t && !seen.has(norm(t))) seen.set(norm(t), t); }
  return Array.from(seen.values()).sort(numericAware);
}

/** "Head table=12, Table 5=6" (or one per line) into table name -> seats. Bad parts are skipped. */
export function parseCapacities(text: string): Map<string, number> {
  const out = new Map<string, number>();
  for (const part of text.split(/[,;\n]+/)) {
    const m = /^(.+?)\s*[=:]\s*(\d{1,3})$/.exec(part.trim());
    if (!m) continue;
    const n = Number(m[2]);
    if (n >= 1 && n <= 500) out.set(norm(m[1]), n);
  }
  return out;
}

export function buildTables(guests: readonly Guest[], count: number, capacity: number, overrides: ReadonlyMap<string, number> = new Map()): TableView[] {
  const list = attending(guests);
  const names = tableNames(guests, count);
  const known = new Set(names.map(norm));
  Array.from(overrides.keys()).forEach((k) => { if (!known.has(k)) names.push(k.replace(/\b\w/g, (c) => c.toUpperCase())); });
  names.sort(numericAware);
  return names.map((name) => {
    const capacity_ = overrides.get(norm(name)) ?? capacity;
    const here = list.filter((g) => norm(g.table) === norm(name));
    const used = here.reduce((s, g) => s + seatsFor(g), 0);
    return { name, guests: here, used, capacity: capacity_, free: capacity_ - used };
  });
}

export const unseated = (guests: readonly Guest[]) => attending(guests).filter((g) => !g.table?.trim());

export interface SeatIssue { level: "error" | "warning"; message: string }
export function seatingIssues(tables: readonly TableView[], guests: readonly Guest[], capacity: number): SeatIssue[] {
  const biggest = Math.max(capacity, ...tables.map((t) => t.capacity));
  const out: SeatIssue[] = [];
  for (const t of tables) if (t.free < 0) out.push({ level: "error", message: `${t.name} is over by ${-t.free} seat${t.free === -1 ? "" : "s"}.` });
  for (const g of attending(guests)) if (seatsFor(g) > biggest) out.push({ level: "error", message: `${g.name}'s party of ${seatsFor(g)} is larger than a table of ${biggest}.` });
  for (const m of ruleBreaks(guests)) out.push({ level: "warning", message: m });
  const n = unseated(guests).length;
  if (n) out.push({ level: "warning", message: `${n} guest${n === 1 ? " has" : "s have"} no table yet.` });
  return out;
}

export interface AutoSeatResult { map: Map<string, string>; /** Rules that could not be met, in plain words. */ unmet: string[] }

const squashLabel = (s: string | undefined) => (s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

/**
 * Seat everyone without a table, filling tables evenly, and say which rules could not be met.
 *
 * - Guests with the same "keep together" label are seated as one group first; guests with no label are grouped by
 *   organisation instead, as a preference only. The biggest groups go first, each at the emptiest open table with room.
 * - A "keep apart" pair is never put at one table. A guest who can only go where someone they avoid sits is left
 *   without a table and named in the report.
 * - A group too big for any table is packed as full tables first (8 + 2), not dealt out one by one.
 * - Only as many tables as the party needs are used (never fewer than two when there are two or more people), so a
 *   small party is spread over a couple of tables. Guests who already have a table stay put.
 */
export function autoSeatWithReport(guests: readonly Guest[], count: number, capacity: number, overrides: ReadonlyMap<string, number> = new Map()): AutoSeatResult {
  const tables = buildTables(guests, count, capacity, overrides);
  const free = new Map(tables.map((t) => [t.name, t.free]));
  const cap = new Map(tables.map((t) => [t.name, t.capacity]));
  const names = tables.map((t) => t.name);
  const unmet: string[] = [];
  const apart = apartIds(guests);
  const occupants = new Map(tables.map((t) => [t.name, new Set(t.guests.map((x) => x.id))]));
  const clash = (table: string, members: readonly Guest[]) => {
    const here = occupants.get(table);
    if (!here) return false;
    return members.some((m) => { const a = apart.get(m.id); return !!a && Array.from(here).some((o) => a.has(o)); });
  };
  const inside = (members: readonly Guest[]) => members.some((m, i) => members.slice(i + 1).some((o) => apart.get(m.id)?.has(o.id)));
  const seats = (xs: readonly Guest[]) => xs.reduce((n, x) => n + seatsFor(x), 0);

  interface Unit { members: Guest[]; label?: string; preferred?: string }
  const labelled = new Map<string, Guest[]>();
  const byOrg = new Map<string, Guest[]>();
  const solos: Guest[][] = [];
  for (const x of unseated(guests)) {
    const lk = squashLabel(x.together);
    if (lk) labelled.set(lk, [...(labelled.get(lk) ?? []), x]);
    else if (x.organisation?.trim()) byOrg.set(norm(x.organisation), [...(byOrg.get(norm(x.organisation)) ?? []), x]);
    else solos.push([x]);
  }
  const seatedAt = (lk: string) => {
    const c = new Map<string, number>();
    for (const x of attending(guests)) if (squashLabel(x.together) === lk && x.table?.trim()) c.set(norm(x.table), (c.get(norm(x.table)) ?? 0) + 1);
    const top = Array.from(c.entries()).sort((a, b) => b[1] - a[1])[0];
    return top ? names.find((n) => norm(n) === top[0]) : undefined;
  };
  const bySize = (a: Unit, b: Unit) => seats(b.members) - seats(a.members);
  const labelUnits: Unit[] = Array.from(labelled.entries()).map(([lk, members]) => ({ members, label: members[0].together?.trim(), preferred: seatedAt(lk) }));
  const orgUnits: Unit[] = [...Array.from(byOrg.values()), ...solos].map((members) => ({ members }));
  const units = [...labelUnits.sort(bySize), ...orgUnits.sort(bySize)];

  const needSeats = units.reduce((n, u) => n + seats(u.members), 0);
  const usedSeats = tables.reduce((n, t) => n + t.used, 0);
  const wanted = Math.min(names.length, Math.max(needSeats > 1 ? 2 : 1, Math.ceil((usedSeats + needSeats) / Math.max(1, capacity))));
  const open = new Set(tables.filter((t) => t.used > 0).map((t) => t.name));
  for (const n of names) { if (open.size >= wanted) break; open.add(n); }
  const fill = (n: string) => ((cap.get(n) ?? 1) - (free.get(n) ?? 0)) / Math.max(1, cap.get(n) ?? 1);
  /** The emptiest open table that fits `need` seats without a clash, falling back to any such table. */
  const pick = (need: number, members: readonly Guest[]): string | undefined => {
    const fits = (n: string) => (free.get(n) ?? 0) >= need && !clash(n, members);
    let best: string | undefined;
    for (const n of names) if (open.has(n) && fits(n) && (best === undefined || fill(n) < fill(best))) best = n;
    return best ?? names.find(fits);
  };
  const result = new Map<string, string>();
  const place = (members: readonly Guest[], table: string) => {
    free.set(table, (free.get(table) ?? 0) - seats(members));
    members.forEach((m) => { result.set(m.id, table); occupants.get(table)?.add(m.id); });
  };
  const putEach = (members: readonly Guest[]) => {
    for (const m of members) {
      const t = pick(seatsFor(m), [m]);
      if (t) place([m], t);
      else unmet.push(`${m.name} has no table: ${seatsFor(m) > Math.max(0, ...names.map((n) => cap.get(n) ?? 0)) ? "the party is larger than any table" : "every table with room has someone they should be kept apart from"}.`);
    }
  };
  /** Full tables first: fill the roomiest table with as many members as fit, repeat, then keep the rest together. */
  const pack = (members: readonly Guest[]): Guest[][] => {
    const left = [...members].sort((a, b) => seatsFor(b) - seatsFor(a));
    const chunks: Guest[][] = [];
    const roomiest = Math.max(0, ...names.map((n) => free.get(n) ?? 0));
    while (left.length && seats(left) > roomiest) {
      const t = names.filter((n) => (free.get(n) ?? 0) > 0).sort((a, b) => (free.get(b) ?? 0) - (free.get(a) ?? 0) || (names.indexOf(a) - names.indexOf(b)))[0];
      if (!t) break;
      const chunk: Guest[] = [];
      let room = free.get(t) ?? 0;
      for (let k = 0; k < left.length; k++) {
        const m = left[k];
        if (seatsFor(m) <= room && !clash(t, [...chunk, m]) && !inside([...chunk, m])) { chunk.push(m); room -= seatsFor(m); left.splice(k, 1); k--; }
      }
      if (!chunk.length) break;
      place(chunk, t);
      chunks.push(chunk);
    }
    if (left.length) chunks.push(left);
    return chunks;
  };

  for (const u of units) {
    const { members, label } = u;
    const total = seats(members);
    const together = label !== undefined && (members.length > 1 || u.preferred !== undefined);
    const groupName = members.map((m) => m.name).join(" + ");
    if (members.length === 1 && !together) { putEach(members); continue; }
    if (label !== undefined && inside(members)) {
      unmet.push(`${groupName} are in one group but some of them are marked to be kept apart, so they were seated separately.`);
      putEach(members); continue;
    }
    if (u.preferred && (free.get(u.preferred) ?? 0) >= total && !clash(u.preferred, members)) { place(members, u.preferred); continue; }
    const whole = inside(members) ? undefined : pick(total, members);
    if (whole) { place(members, whole); continue; }
    // The group cannot sit at one table.
    const parts = pack(members);
    const placedParts = parts.filter((c) => c.every((m) => result.has(m.id)));
    const rest = parts.filter((c) => !placedParts.includes(c)).flat();
    const restTable = rest.length > 1 ? pick(seats(rest), rest) : undefined;
    if (restTable && !inside(rest)) place(rest, restTable); else putEach(rest);
    if (label !== undefined) {
      const tablesUsed = Array.from(new Set(members.map((m) => result.get(m.id)).filter(Boolean)));
      const biggest = Math.max(0, ...names.map((n) => cap.get(n) ?? 0));
      const why = total > biggest ? `${total} seats are more than a table of ${biggest}` : "no single table has room for them";
      unmet.push(`${groupName} could not sit together (${why})${tablesUsed.length ? `; they are at ${tablesUsed.join(" and ")}` : ""}.`);
    }
  }
  return { map: result, unmet };
}

/** Seat everyone without a table. See autoSeatWithReport for the rules; this returns only the seating. */
export function autoSeat(guests: readonly Guest[], count: number, capacity: number, overrides: ReadonlyMap<string, number> = new Map()): Map<string, string> {
  return autoSeatWithReport(guests, count, capacity, overrides).map;
}

export function seatingRows(tables: readonly TableView[], loose: readonly Guest[]): string[][] {
  const rows: string[][] = [["Table", "Guest", "Organisation", "Seats", "Dietary", "Access"]];
  for (const t of tables) for (const g of t.guests) rows.push([t.name, g.name, g.organisation ?? "", String(seatsFor(g)), g.dietary ?? "", g.access ?? ""]);
  for (const g of loose) rows.push(["", g.name, g.organisation ?? "", String(seatsFor(g)), g.dietary ?? "", g.access ?? ""]);
  return rows;
}

export function seatingText(tables: readonly TableView[], loose: readonly Guest[]): string {
  const parts = tables.filter((t) => t.guests.length).map((t) => `${t.name} (${t.used}/${t.capacity})\n${t.guests.map((g) => `  ${g.name}${g.plusOnes ? ` +${g.plusOnes}` : ""}`).join("\n")}`);
  if (loose.length) parts.push(`Not seated\n${loose.map((g) => `  ${g.name}`).join("\n")}`);
  return parts.join("\n\n");
}
